import YatungButton from '@Src/_basic/components/YatungButton';
import YatungFadeInOut from '@Src/_basic/components/YatungFadeInOut';
import YatungPage from '@Src/_basic/components/YatungPage';
import { updateReducer } from '@Src/_basic/helpers/useUpdateReducer';
import NewStaffIcon from '@Src/_basic/icons/NewStaff';
import { PageInfo } from '@Src/_basic/object/PageInfoType';
import {
  SearchThirdPartyAccountResponse,
  ThirdPartyAccountData,
  ThirdPartyAccountTableData,
} from '@Src/_basic/object/ThirdPartyAccountType';
import { ThirdPartyAccountApi } from '@Src/_basic/protocol/accountRights/thirdPartyAccount/ThirdPartyAccountApi';
import { useRequestSaving } from '@Src/redux/requestSaving/requestSavingActions';
import { Grid, Pagination, Stack } from '@mui/material';
import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import ThirdPartyAccountManagementTable from './ThirdPartyAccountManagementTable';

type Query = {
  page: number;
};

const PAGE_SIZE = 20;

export default function ThirdPartyAccountManagementCard() {
  const { t: i18T } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const { setSaveRequest, request } = useRequestSaving<Query>();

  const initPageInfo = useMemo(
    () => ({
      page: location.pathname === request?.page && request?.request ? request.request.page : 1,
      pageSize: PAGE_SIZE,
      total: 0,
      totalCount: 0,
    }),
    [location.pathname, request],
  );

  const [pageInfo, pageInfoDispatch] = useReducer<Reducer<PageInfo, Partial<PageInfo>>>(updateReducer, initPageInfo);

  const effectRef = useRef(false);
  const [isVisable, setIsVisable] = useState<boolean>(false);
  const [accountData, setAccountData] = useState<Array<ThirdPartyAccountTableData>>([]);

  const getAllAccount = useCallback(
    (page: number) => {
      ThirdPartyAccountApi.searchAccounts(
        {
          pageNumber: page - 1,
          pageSize: PAGE_SIZE,
          sourceKinds: [5],
        },
        (_data: SearchThirdPartyAccountResponse) => {
          setAccountData(
            (_data?.content ?? []).map((item: ThirdPartyAccountData, index: number) => ({
              ...item,
              index: index + 1 + (page - 1) * PAGE_SIZE,
            })),
          );
          pageInfoDispatch({ total: _data?.totalPages ?? 0 });
          setSaveRequest({
            page: location.pathname,
            request: { page },
          });
          setIsVisable(true);
        },
      );
    },
    [location.pathname, setSaveRequest],
  );

  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    pageInfoDispatch({ page });
    getAllAccount(page);
  };

  const handleGoToCreatePage = () => {
    navigate('/UsersManagePage/ThirdPartyAccountManagement/ThirdPartyAccountCreate');
  };

  useEffect(() => {
    if (!effectRef.current) {
      effectRef.current = true;
      getAllAccount(pageInfo.page);
    }
  }, [effectRef]);

  return (
    <YatungPage
      title={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ROUTE_TITLE')}
      actions={
        <Stack direction="row" spacing={2} alignItems="center">
          <YatungButton
            text={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.CREATE_ACCOUNT')}
            variant={'contained'}
            type={'button'}
            size={'large'}
            startIcon={<NewStaffIcon />}
            onClick={handleGoToCreatePage}
            color={'green'}
            sx={{ fontSize: '16px' }}
          />
        </Stack>
      }
      body={
        <YatungFadeInOut isVisible={isVisable}>
          <>
            <ThirdPartyAccountManagementTable accountData={accountData} />
            <Grid container justifyContent={'center'} item xs={11} sx={{ mt: 2 }}>
              <Pagination count={pageInfo.total} page={pageInfo.page} onChange={handlePageChange} color="primary" />
            </Grid>
          </>
        </YatungFadeInOut>
      }
      contentBgColor="#FFF"
    />
  );
}
